import { Base, Collection } from 'active-resource';
import { JobLabelVariant } from 'features/JobLabel/JobLabel.types';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';
import { formattedAddress } from 'util/formatting/address';
import { dollarsToUsdFormat, wageDollarsFormat } from 'util/formatting/currency';
import { withinAWeek } from 'util/formatting/dates';
import { EducationalQualificationResource } from 'util/resources/EducationalQualification';
import { JobApplicationResource } from 'util/resources/JobApplication';
import { LocationResource } from 'util/resources/Location';
import APILibrary, { ClassExtender, GConstructor, GenerateResource } from './APILibrary';
import { EmployerResource } from './Employer';
import { AI_ASSISTED_STATUSES } from 'util/AiAssistedStatuses';

/**
 * Relationship classes
 */
export class JobListingAttributes {
  additionalBenefits: string[] = null;
  aiAssisted?: AI_ASSISTED_STATUSES;
  ageGroup: string[] = null;
  certificationQualifications: {
    achievement?: string;
    inProgressAllowed?: boolean;
    required?: boolean;
  }[] = null;
  classroomType: string[] = null;
  compensation: {
    min?: number | string;
    max?: number | string;
    interval?: string;
  } = null;
  createdAt: string = null;
  description: string = null;
  desiredEducation: string[] = null;
  educationPhilosophy: {
    educationPhilosophy?: string[];
    required?: boolean;
  } = null;
  educationalQualifications: EducationalQualificationResource[] = null;
  employmentSubtype: string = null;
  employmentType: string[] = null;
  englishProficiency: {
    englishProficiency?: string;
  } = null;
  externalUrl: string = null;
  jobApplicationsCount: number = null;
  hireTimeline: string = null;
  id: string = null;
  liked: boolean = null;
  jobSchedule: string[] = null;
  languages: {
    languages: string;
    required?: boolean;
  }[] = null;
  listingStatus: string = null;
  locationIds: string[] = null;
  minAge: string = null;
  publishedAt: string = null;
  qualificationsDescription: string = null;
  roleCount: number = null;
  roleType: string[] = null;
  screeningQuestions: string = null;
  slug: string = null;
  startTimeline: string = null;
  supplementalPay: {
    signingBonus?: number | string;
    bonus?: number | string;
    other?: number | string;
  } = null;
  title: string = null;
  updatedAt: string = null;
  workExperience: {
    workExperience: string;
    yearsExperience?: string;
    required?: boolean;
  }[] = null;
}

export class JobListingBelongsTo {
  employer: () => EmployerResource = () => new EmployerResource();
}

export class JobListingHasMany {
  jobApplications: () => Collection<JobApplicationResource> = () =>
    new Collection<JobApplicationResource>();
  locations: () => Collection<LocationResource> = () => new Collection<LocationResource>();
}

/**
 * Utility interfaces
 */
export interface JobListingMethods {
  ageGroups: () => string;
  getCompensationText: () => {
    salaryRange?: string;
    hiringBonus?: string;
    bonus?: string;
  };
  firstLocation: () => LocationResource;
  getLocationText: (condensed?: boolean, location?: LocationResource) => string;
  isNew: () => boolean;
  getLabel: (jobRecommendations: {
    list: JobListingResource[];
  }) => JobLabelVariant.RECOMMENDED_FOR_YOU | JobLabelVariant.NEW_JOB;
  toJobCardFormat: (
    condensed: boolean,
    jobRecommendations: {
      list: JobListingResource[];
    },
    location?: LocationResource,
  ) => {
    title: string;
    location: {
      name: string;
      img: string;
    };
    features: {
      featureType: string;
      value: string;
    }[];
    jobApplications: JobApplicationResource[];
    benefits: string[];
    label: JobLabelVariant.RECOMMENDED_FOR_YOU | JobLabelVariant.NEW_JOB;
  };
}

export interface JobListingInterface
  extends JobListingAttributes,
    JobListingBelongsTo,
    JobListingHasMany,
    JobListingType {}

type JobListingType = GConstructor<JobListingInterface>;

export interface JobListingResource
  extends Base<JobListingAttributes>,
    JobListingInterface,
    JobListingMethods {}

/**
 * Resource methods
 */
export const BaseClass = () =>
  GenerateResource<JobListingAttributes>({
    queryName: 'job_listings',
    className: 'JobListing',
    AttributesClass: JobListingAttributes,
    BelongsToClasses: [
      {
        Klass: JobListingBelongsTo,
      },
    ],
    HasManyClasses: [
      {
        Klass: JobListingHasMany,
      },
    ],
  });

export const JobListingResourceClass = <TBase extends JobListingType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements JobListingMethods {
    ageGroups() {
      return this.ageGroup.join(', ');
    }

    getCompensationText() {
      const compensationData: {
        salaryRange?: string;
        hiringBonus?: string;
        bonus?: string;
      } = {};
      const { min, max, interval } = this.compensation || {};

      if ((!min || min === '0') && (!max || max === '0')) {
        return compensationData;
      }

      const { signingBonus, bonus } = this.supplementalPay;

      let range = `${wageDollarsFormat(min, interval)} - ${wageDollarsFormat(max, interval)}`;

      if (min === max || !min || min === '0' || !max || max === '0') {
        if (!min || min === '0') {
          range = `${wageDollarsFormat(max, interval)}`;
        } else {
          range = `${wageDollarsFormat(min, interval)}`;
        }
      }

      compensationData.salaryRange = `${range} / ${interval}`;
      if (isNumber(+signingBonus) && Boolean(+signingBonus)) {
        compensationData.hiringBonus = `Hiring Incentive: ${dollarsToUsdFormat(signingBonus, {
          maximumFractionDigits: 0,
        })}`;
      }

      if (isNumber(+bonus) && Boolean(+bonus)) {
        compensationData.bonus = `Hiring Incentive: ${dollarsToUsdFormat(bonus, {
          maximumFractionDigits: 0,
        })}`;
      }

      return compensationData;
    }

    firstLocation() {
      return this.locations().toArray()[0];
    }

    getLocationText(condensed, location = this.firstLocation()) {
      if (condensed) {
        if (!location) return;

        const address = formattedAddress(location);
        const numLocations = this.locationIds.length;
        return `${address} ${numLocations > 1 ? `(+${numLocations - 1} more locations)` : ''}`;
      }
      const neighborhoods = uniq(
        this.locations()
          .toArray()
          .map(({ neighborhood }) => neighborhood),
      );
      const numNeighborhoods = neighborhoods.length;

      if (!numNeighborhoods || !neighborhoods[0]) return;

      return `${neighborhoods[0]} ${numNeighborhoods > 1 ? `(+${numNeighborhoods - 1} more)` : ''}`;
    }

    isNew() {
      return withinAWeek(this.publishedAt);
    }

    getLabel(
      jobRecommendations: {
        list: JobListingResource[];
      } = {
        list: [],
      },
    ) {
      const recommended = jobRecommendations?.list?.find(({ id }) => id === this.id);
      const isNew = this.isNew();
      if (recommended) return JobLabelVariant.RECOMMENDED_FOR_YOU;
      if (isNew) return JobLabelVariant.NEW_JOB;
    }

    toJobCardFormat(
      condensed,
      jobRecommendations = { list: [] },
      location: LocationResource = null,
    ) {
      const employer = this.employer();
      const employerName = employer?.name;
      const employerLogo = employer?.profileImage?.downloadUrl;
      const employmentType = this.employmentType.join(', ');
      const { salaryRange, hiringBonus } = this.getCompensationText();
      const locationVal = this.getLocationText(condensed, location);
      const ageGroup = this.ageGroup.join(' / ');

      const features = [];

      locationVal && features.push({ featureType: 'location', value: locationVal });
      !condensed && !!ageGroup.length && features.push({ featureType: 'family', value: ageGroup });
      employmentType && features.push({ featureType: 'commitment', value: employmentType });
      salaryRange && features.push({ featureType: 'salary', value: salaryRange });
      hiringBonus && features.push({ featureType: 'financial', value: hiringBonus });

      const jobData = {
        id: this.id,
        title: this.title,
        liked: this.liked,
        location: {
          name: location?.name || employerName,
          img: employerLogo,
        },
        features,
        jobApplications: this.jobApplications().toArray(),
        benefits: undefined,
        label: undefined,
      };

      if (!condensed) jobData.benefits = this.additionalBenefits;

      jobData.label = this.getLabel(jobRecommendations);

      return jobData;
    }
  };

export class JobListingResource extends ClassExtender<JobListingInterface>(
  BaseClass,
  JobListingResourceClass,
) {}

/**
 * Resource instance
 */
export const JobListing = APILibrary.createResource<JobListingResource>(JobListingResource);
